import {
  fullSearchTrunkSize,
  fullSearchSubTrunkSize,
} from 'modules/constants'

/** Soft ceiling so a buggy full-page response cannot loop forever. */
export const EXPORT_HARD_ROW_CAP = 500_000

export type FullSearchExportPost = (
  route: string,
  body: { params: Record<string, unknown>; config: unknown },
) => Promise<{ data: Record<string, unknown> }>

export type FullSearchExportResult = {
  [dataType: string]: unknown[]
} & {
  exportFetched: number
  exportExpected: number
}

/**
 * Full-search trunking for CSV export only.
 * - Forces facets: [] on every sub-request.
 * - Does not clear fullSearchStatus when Solr finishes (caller clears after download).
 * - Continues until a trunk returns fewer than trunkSize rows (does not trust maxLimit alone).
 * - Optional onTrunk: process each trunk and drop it (avoids retaining all rows).
 */
export const runFullSearchExport = async ({
  post,
  isCancelled,
  route,
  dataTypes,
  params,
  config,
  maxLimit,
  setFullSearchStatus,
  onTrunk,
  trunkSize = fullSearchTrunkSize,
  subTrunkSize = fullSearchSubTrunkSize,
  hardRowCap = EXPORT_HARD_ROW_CAP,
}: {
  post: FullSearchExportPost
  isCancelled: () => boolean
  route: string
  dataTypes: string[]
  params: Record<string, unknown>
  config: unknown
  maxLimit: number
  setFullSearchStatus?: (status: Record<string, unknown>) => void
  onTrunk?: (
    trunkRowsByType: Record<string, unknown[]>,
    trunkIndex: number,
  ) => void | Promise<void>
  trunkSize?: number
  subTrunkSize?: number
  hardRowCap?: number
}): Promise<FullSearchExportResult> => {
  const exportParams = {
    ...params,
    facets: [],
    page_action: true,
    rows: trunkSize,
    start: (params.start as number) || 0,
  }

  const output: Record<string, unknown[]> = {}
  for (const cur of dataTypes) {
    output[cur] = []
  }

  const expected = maxLimit || 0
  let fetched = 0
  let progressTotal = expected

  if (typeof setFullSearchStatus === 'function') {
    setFullSearchStatus({
      state: 'in-progress',
      total: progressTotal,
      fetched: 0,
    })
  }

  let trunkIndex = 1
  while (true) {
    let reachedNaturalEnd = false
    try {
      if (isCancelled()) {
        throw ('cancelled')
      }

      const trunkResponses = (await Promise.all([0, 1, 2].map(subIndex => (
        post(route, {
          params: {
            ...exportParams,
            facets: [],
            page_action: true,
            rows: subTrunkSize,
            start: exportParams.start + subTrunkSize * subIndex,
          },
          config,
        })
      )))).map(r => r.data)

      if (isCancelled()) {
        throw ('cancelled')
      }

      const trunkRowsByType: Record<string, unknown[]> = {}
      let trunkRowCount = 0
      for (const cur of dataTypes) {
        trunkRowsByType[cur] = []
        for (const trunkResponse of trunkResponses) {
          const chunk = (trunkResponse[cur] as unknown[]) || []
          for (let i = 0; i < chunk.length; i++) {
            trunkRowsByType[cur].push(chunk[i])
          }
        }
        // Progress / short-trunk checks use the primary data type (orders).
        if (cur === dataTypes[0]) {
          trunkRowCount = trunkRowsByType[cur].length
        }
      }

      if (typeof onTrunk === 'function') {
        await onTrunk(trunkRowsByType, trunkIndex)
      } else {
        for (const cur of dataTypes) {
          for (let i = 0; i < trunkRowsByType[cur].length; i++) {
            output[cur].push(trunkRowsByType[cur][i])
          }
        }
      }

      fetched += trunkRowCount
      exportParams.start += trunkSize

      reachedNaturalEnd = trunkRowCount < trunkSize
      if (!reachedNaturalEnd) {
        progressTotal = Math.max(progressTotal, fetched + trunkSize)
      } else if (progressTotal < fetched) {
        progressTotal = fetched
      }

      if (typeof setFullSearchStatus === 'function') {
        setFullSearchStatus({
          fetched,
          total: Math.max(progressTotal, fetched),
        })
      }
    } catch (error) {
      if (error === 'cancelled') {
        throw ('cancelled')
      }
      console.error(`Failed to get CSV export trunk ${trunkIndex}`, error)
      if (typeof setFullSearchStatus === 'function') {
        setFullSearchStatus({
          state: 'failed',
          message: 'Export failed while fetching records. Try a smaller date range or a single store.',
        })
      }
      throw error
    }

    if (reachedNaturalEnd) {
      break
    }

    if (fetched >= hardRowCap) {
      const message = `Export stopped after ${fetched.toLocaleString()} records (safety limit). Try a smaller date range or a single store.`
      if (typeof setFullSearchStatus === 'function') {
        setFullSearchStatus({ state: 'failed', message })
      }
      throw new Error(message)
    }

    trunkIndex += 1
  }

  return {
    ...output,
    exportFetched: fetched,
    exportExpected: expected,
  }
}
